import { useEffect, useRef, useState } from 'react';
import { DEVICE_PIXEL_RATIO, FONT } from 'src/lib/player/constants';
import { t } from 'src/lib/player/i18n';
import IconMapping from 'src/lib/player/iconmapping.json';
import { type PoiType, useStore } from 'src/lib/player/store';
import { formatDistance, noop, realSubstring } from 'src/lib/player/utils';
import MarkerContext, { POI_HEIGHT, POI_WIDTH } from '../Map/MarkerContext';
import { createPoiDrawInfo } from '../Map/createDrawInfos';
import { MapFeatureListItem } from './MapFeatureListItem';

type Props = {
  poi: PoiType;
  onClick: (poi: PoiType, isSelected: boolean) => void;
};

function drawPOI(
  poi: PoiType,
  canvas: HTMLCanvasElement,
  tickless: boolean,
  scale: number = DEVICE_PIXEL_RATIO,
): Promise<void> {
  return new Promise((resolve) => {
    const ctx = canvas.getContext('2d');
    if (ctx == null) {
      throw new Error("Couldn't create drawing canvas");
    }

    ctx.font = FONT;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    if (poi.icon != null) {
      const isStandardIcon = Object.keys(IconMapping).includes(poi.icon);
      let icon = poi.icon;
      if (isStandardIcon) {
        icon = tickless
          ? `/img/map-icons/${poi.icon}_tickless.svg`
          : `/img/map-icons/${poi.icon}.svg`;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width / scale, canvas.height / scale);
        resolve();
      };
      img.src = icon;
    } else {
      const drawInfo = createPoiDrawInfo(
        poi.id,
        poi.shortName ?? realSubstring(poi.name, 0, 3),
        poi.name,
        poi.latlng,
        poi.markerColor?.hex ?? 'white',
      );
      drawInfo.tickless = tickless;
      MarkerContext.draw(drawInfo, ctx, 0, 0, 1);
      resolve();
    }
  });
}

export function PoiIconSVG({
  poi,
  tickless = false,
}: {
  poi: PoiType;
  tickless?: boolean;
}): JSX.Element {
  const [poiImageUrl, setPoiImageUrl] = useState('');

  useEffect(() => {
    (async () => {
      const canvas = document.createElement('canvas');
      canvas.width = POI_WIDTH * DEVICE_PIXEL_RATIO;
      canvas.height = POI_HEIGHT * DEVICE_PIXEL_RATIO;
      await drawPOI(poi, canvas, tickless, DEVICE_PIXEL_RATIO);
      setPoiImageUrl(canvas.toDataURL('image/png'));
    })();
  }, [poi, tickless]);
  return <image href={poiImageUrl} width={POI_WIDTH} height={POI_HEIGHT} />;
}

export function PoiIcon({
  poi,
  tickless = false,
  scale = 0.5,
}: {
  poi: PoiType;
  tickless?: boolean;
  scale?: number;
}): JSX.Element {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas == null) return;

    drawPOI(poi, canvas, tickless, DEVICE_PIXEL_RATIO);
  }, [poi, tickless]);

  return (
    <canvas
      ref={canvasRef}
      width={POI_WIDTH * DEVICE_PIXEL_RATIO}
      height={POI_HEIGHT * DEVICE_PIXEL_RATIO}
      style={{ width: POI_WIDTH * scale, height: POI_HEIGHT * scale }}
    />
  );
}

export default function PoiListItem({ poi, onClick = noop }: Props): JSX.Element {
  const unitType = useStore((state) => state.options.unitType);

  return (
    <MapFeatureListItem
      id={poi.id}
      kind="poi"
      label={poi.name || t('poi')}
      icon={<PoiIcon poi={poi} tickless />}
      info={
        poi.offset != null && poi.offset >= 0 ? (
          <span>{formatDistance(poi.offset, unitType)}</span>
        ) : undefined
      }
      onClick={(isSelected) => onClick(poi, isSelected)}
    />
  );
}
